import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background" data-testid="error-boundary-fallback">
        <div className="max-w-md w-full border rounded-lg p-6 space-y-4 bg-card">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-destructive" />
            <h1 className="text-lg font-semibold">Something went wrong</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            An unexpected error occurred. Reload the page to continue.
          </p>
          {this.state.error?.message ? (
            <pre className="text-xs bg-muted p-2 rounded font-mono overflow-x-auto whitespace-pre-wrap">
              {this.state.error.message}
            </pre>
          ) : null}
          <Button onClick={this.handleReload} data-testid="button-error-reload">Reload</Button>
        </div>
      </div>
    );
  }
}
